// TimetableModal.jsx - Professional Redesign
import React, { useState } from 'react';
import { X, Download, Printer, Maximize2, Minimize2, Calendar } from 'lucide-react';
import Timetable from './Timetable';

const TimetableModal = ({ isOpen, onClose, facultyId, tenantId }) => {
  const [fullscreen, setFullscreen] = useState(false);

  if (!isOpen) return null;

  const handlePrint = () => {
    const printContent = document.getElementById('timetable-print-content');
    const originalTitle = document.title;
    document.title = 'Teaching Timetable';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Teaching Timetable</title>
          <meta charset="utf-8" />
          <style>
            body {
              font-family: 'Inter', system-ui, -apple-system, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            @media print {
              body {
                padding: 0;
              }
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="no-print" style="text-align: center; margin-bottom: 20px;">
            <button onclick="window.print();window.close();" style="padding: 10px 20px; margin: 0 10px;">Print</button>
            <button onclick="window.close();" style="padding: 10px 20px;">Close</button>
          </div>
          <div id="print-content">
            ${printContent ? printContent.innerHTML : ''}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    
    document.title = originalTitle;
  };

  const handleExport = () => {
    // Create a temporary div with the timetable content for export
    const exportContent = document.getElementById('timetable-print-content');
    if (exportContent) {
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Teaching Timetable Export</title>
            <meta charset="utf-8" />
            <style>
              body {
                font-family: 'Inter', system-ui, -apple-system, sans-serif;
                margin: 20px;
                background: white;
              }
              table {
                width: 100%;
                border-collapse: collapse;
              }
              th, td {
                border: 1px solid #e5e7eb;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #f3f4f6;
              }
            </style>
          </head>
          <body>
            ${exportContent.innerHTML}
          </body>
        </html>
      `;
      
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timetable_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 ${fullscreen ? '' : 'overflow-y-auto'}`}>
      {/* Backdrop - Smooth Blur matching your core theme */}
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300" onClick={onClose}></div>

      {/* Modal Container */}
      <div className={`relative bg-white shadow-2xl transform transition-all duration-300 ease-out flex flex-col ${
        fullscreen 
          ? 'fixed inset-0 w-full h-full rounded-none z-50' 
          : 'w-full max-w-7xl h-full sm:h-[85vh] rounded-none sm:rounded-2xl z-50'
      }`}>
        {/* Header - Styled with gradient profile background accent */}
        <div className={`flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-white ${
          fullscreen ? '' : 'sm:rounded-t-2xl'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/20">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800 tracking-tight">
                Master Timetable
              </h3>
              <p className="text-slate-500 text-xs hidden sm:block mt-0.5">Comprehensive weekly structural hour mapping grids</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5">
            {/* Fullscreen Toggle */}
            <button
              onClick={() => setFullscreen(!fullscreen)}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all duration-200"
              title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            
            {/* Export Button */}
            <button
              onClick={handleExport}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all duration-200"
              title="Export as HTML"
            >
              <Download className="w-4 h-4" />
            </button>
            
            {/* Print Button */}
            <button
              onClick={handlePrint}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all duration-200"
              title="Print"
            >
              <Printer className="w-4 h-4" />
            </button>
            
            {/* Divider */}
            <div className="w-px h-5 bg-slate-200 mx-1"></div>
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Timetable Content Body Workspace */}
        <div id="timetable-print-content" className="flex-1 overflow-auto bg-slate-50/50">
          <div className="p-4 sm:p-6">
            <Timetable
              facultyId={facultyId}
              tenantId={tenantId}
              academicYearId={null}
              semesterType={null}
              onDateSelect={() => {}}
              isModal={true}
            />
          </div>
        </div>
        
        {/* Footer - Updated for professional look */}
        <div className={`px-6 py-3.5 border-t border-slate-100 bg-white flex items-center justify-between text-[11px] text-slate-400 font-medium ${
          fullscreen ? '' : 'sm:rounded-b-2xl'
        }`}>
          <div className="flex items-center gap-3">
            <span>Updated: {new Date().toLocaleString([], {hour: '2-digit', minute:'2-digit'})}</span>
            <span className="w-px h-2.5 bg-slate-200"></span>
            <span className="hidden sm:inline">Automatic 5m synchronization</span>
          </div>
          <div className="flex items-center gap-1.5 text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-semibold">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
            <span>Live System</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimetableModal;